import { Component, effect, inject, signal, untracked, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpParams, httpResource } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, RouterModule, Router } from '@angular/router';
import { ListComponent } from '@components/list/list.component';
import { environment } from '@environment';
import { ItemListResponse, PlanktonTaxon } from '@models';

@Component({
  selector: 'app-plankton-taxon-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ListComponent,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTooltipModule,
    RouterModule,
  ],
})
export class PlanktonTaxonListComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  route = inject(ActivatedRoute);
  router = inject(Router);

  planktonDatasetId = environment.planktonDatasetId;

  count = signal(0);
  page = signal(0);
  pageSize = signal(10);
  q = signal('');

  httpParams(): HttpParams {
    const count = untracked(() => this.count() === 0 || this.page() === 0);
    let params = new HttpParams()
      .set('count', count)
      .set('occurrenceCount', true)
      .set('skip', this.page() * this.pageSize())
      .set('take', this.pageSize());
    if (this.q()) {
      params = params.set('q', this.q());
    }
    return params;
  }

  response = httpResource<ItemListResponse<PlanktonTaxon>>(() => {
    return `${environment.kinkoHost}/plankton/taxon/?${this.httpParams()}`;
  });

  constructor() {
    this.route.queryParams.subscribe((params: Params) => {
      this.page.set(parseInt(params['page']) || 0);
      this.pageSize.set(parseInt(params['pageSize']) || 10);
      this.q.set((params['q'] as string) || '');
    });

    effect(() => {
      if (!this.response.hasValue() || !this.response.value().count) {
        return;
      }

      this.count.set(this.response.value().count);
    });
  }

  onPageEvent(event: PageEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        page: event.pageIndex || undefined,
        pageSize: event.pageSize === 10 ? undefined : event.pageSize,
      },
      replaceUrl: true,
    });
  }

  onSearchEvent(event: Event) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: (event.target as HTMLInputElement).value.trim() || undefined,
        page: undefined,
      },
      replaceUrl: true,
    });
  }

  downloadURL(format: string): string {
    let q = this.httpParams().delete('skip').delete('take');
    if (format != '') {
      q = q.delete('count').set('format', format);
    }
    return `${environment.kinkoHost}/plankton/taxon/?${q}`;
  }

  path(taxon: PlanktonTaxon): string {
    if (!taxon.kingdom) {
      return 'N/A';
    }
    let path = taxon.kingdom;
    for (const t of [taxon.phylum, taxon.class, taxon.order, taxon.family, taxon.genus]) {
      if (!t) {
        return path;
      }
      path += ' > ' + t;
    }
    return path;
  }
}
